import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  BadgeDollarSign,
  Building2,
  Compass,
  Handshake,
  HeartHandshake,
  Home,
  Landmark,
  Map,
  ShieldCheck,
  Sparkles,
  Target,
  TrendingUp,
} from "lucide-react";
import SiteLayout from "@/components/layout/SiteLayout";
import GuideDownloadSection from "@/components/sections/GuideDownloadSection";
import { BUYERS_GUIDE } from "@/lib/guides";
import PageHero from "@/components/sections/PageHero";
import ContentSection from "@/components/sections/ContentSection";
import SectionHeader from "@/components/sections/SectionHeader";
import AccordionGroup from "@/components/sections/AccordionGroup";
import SearchFilterThin from "@/components/sections/SearchFilterThin";
import SplitSection from "@/components/sections/SplitSection";
import CardGrid from "@/components/sections/CardGrid";
import ComparisonBlock from "@/components/sections/ComparisonBlock";
import Checklist from "@/components/sections/Checklist";
import TestimonialsSection from "@/components/TestimonialsSection";
import BuyerStrategySection from "@/components/sections/BuyerStrategySection";
import heroImage from "@/assets/about-massachusetts.jpg";
import buyGuidanceTour from "@/assets/buy-guidance-tour.jpg";
import buyProcessConsultation from "@/assets/buy-process-consultation.jpg";
import buyFirstTimeBuyer from "@/assets/buy-first-time-buyer.jpg";
import buyFinancialGuidance from "@/assets/buy-financial-guidance.jpg";
import buyLongTermPerspective from "@/assets/buy-long-term-perspective.jpg";
import buySuccessCompetitiveMarket from "@/assets/buy-success-competitive-market.jpg";
import buySuccessBudget from "@/assets/buy-success-budget.jpg";
import buySuccessBeyondListing from "@/assets/buy-success-beyond-listing.jpg";
import buyDROpportunity from "@/assets/buy-dr-opportunity.jpg";
import buyFinalCta from "@/assets/buy-final-cta.jpg";

const buyingSteps = [
  {
    label: "Step 01",
    title: "Strategy & Consultation",
    description: "We align your goals, timing, and budget before the search begins.",
  },
  {
    label: "Step 02",
    title: "Pre-Approval & Preparation",
    description: "We connect you with trusted lenders so you can move with clarity and speed.",
  },
  {
    label: "Step 03",
    title: "Curated Search & Showings",
    description: "We focus on the right homes, not just more homes, with your long-term fit in mind.",
  },
  {
    label: "Step 04",
    title: "Offer & Negotiation",
    description: "We help you structure a strong offer and navigate every conversation strategically.",
  },
  {
    label: "Step 05",
    title: "Closing & Next Steps",
    description: "We stay with you through closing so the move into your next chapter feels smooth.",
  },
];

const faqItems = [
  {
    title: "Do I need to get pre-approved before starting my home search?",
    content:
      "Yes. Pre-approval helps you understand your budget and shows sellers you're a serious buyer.",
  },
  {
    title: "How long does the home buying process take?",
    content:
      "It varies, but most buyers can expect the process to take 30 to 60 days once an offer is accepted.",
  },
  {
    title: "What should I look for when choosing a home?",
    content:
      "Beyond aesthetics, consider location, long-term value, condition, and future resale potential.",
  },
  {
    title: "Do you work with first-time buyers?",
    content:
      "Absolutely. We specialize in guiding first-time buyers and making the process clear and manageable.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqItems.map((item) => ({
    "@type": "Question",
    name: item.title,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.content,
    },
  })),
};

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "RealEstateAgent",
  name: "Hadassah Homes",
  url: "https://hadassahhomesinc.com",
  areaServed: {
    "@type": "State",
    name: "Massachusetts",
  },
  description:
    "Buy a home in Massachusetts with confidence. Hadassah Homes provides expert guidance, trusted lender connections, and a step-by-step strategy to help you find and secure the right home.",
};

const workWithUsFocus = [
  {
    title: "Goals First",
    description: "Your lifestyle, timing, and long-term plans shape the strategy from day one.",
  },
  {
    title: "Strategic Search",
    description: "We narrow the search to homes with real fit, value, and future potential.",
  },
  {
    title: "Clear Guidance",
    description: "You get honest advice and strong market context at every decision point.",
  },
  {
    title: "Competitive Edge",
    description: "We help you act with confidence in fast-moving Massachusetts markets.",
  },
];

const firstTimeBuyerItems = [
  {
    icon: Map,
    title: "The full process",
    description: "Know what happens next from consultation to closing.",
  },
  {
    icon: Landmark,
    title: "Your real budget",
    description: "Get clear on what you can comfortably afford before emotions take over.",
  },
  {
    icon: Home,
    title: "What matters most",
    description: "Learn how to spot fit, value, and long-term potential quickly.",
  },
  {
    icon: HeartHandshake,
    title: "Confident decisions",
    description: "Move forward with support instead of carrying the pressure alone.",
  },
];

const buyerValues = [
  {
    icon: Target,
    title: "Strategic guidance with honest perspective",
    description: "A buying experience designed around clarity, pacing, and smart decisions, with advice centered on fit, value, and confidence rather than pressure.",
  },
  {
    icon: Building2,
    title: "Massachusetts insight",
    description: "Strong local knowledge that helps you focus on the right neighborhoods.",
  },
  {
    icon: BadgeDollarSign,
    title: "Trusted financial partners",
    description: "Connections to lenders and professionals that keep you ready to move.",
  },
  {
    icon: Handshake,
    title: "Relationship-first service",
    description: "A more personal, guided experience from first showing through closing.",
  },
];

const buyerSuccesses = [
  {
    title: "Winning in a competitive market",
    description:
      "A first-time buyer secured the right home with a strong offer strategy and calm, informed decision-making.",
    image: buySuccessCompetitiveMarket,
  },
  {
    title: "Staying aligned with budget",
    description:
      "We guided a buyer through financing and negotiation so they could secure the right fit without stretching too far.",
    image: buySuccessBudget,
  },
  {
    title: "Thinking beyond the listing",
    description:
      "A client identified a property with stronger long-term upside because the search stayed focused on value, not hype.",
    image: buySuccessBeyondListing,
  },
];

const buyerTestimonials = [
  {
    name: "First-Time Buyer",
    text: "Esther made the entire process feel clear and manageable. As first-time buyers, we had a lot of questions, and she guided us through every step with patience and confidence.",
  },
  {
    name: "Massachusetts Buyer",
    text: "Working with Hadassah Homes was the best decision we made. We felt informed, supported, and confident from start to finish.",
  },
  {
    name: "Growing Family Buyer",
    text: "We needed more than help finding listings. We needed strategy, honest feedback, and someone who understood what would make sense for our family long-term. Hadassah Homes delivered exactly that.",
  },
];

const showPropertySearch = false;

const BuyAHome = () => {
  useEffect(() => {
    document.title = "Buy a Home in Massachusetts | Hadassah Homes Real Estate";
    const meta = document.querySelector('meta[name="description"]');
    if (meta)
      meta.setAttribute(
        "content",
        "Buy a home in Massachusetts with confidence. Hadassah Homes provides expert guidance, trusted lender connections, and a step-by-step strategy to help you find and secure the right home."
      );
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", "https://hadassahhomesinc.com/buy-a-home");

    const faqScriptId = "buy-a-home-faq-schema";
    const lbScriptId = "buy-a-home-lb-schema";
    if (!document.getElementById(faqScriptId)) {
      const s = document.createElement("script");
      s.id = faqScriptId;
      s.type = "application/ld+json";
      s.textContent = JSON.stringify(faqSchema);
      document.head.appendChild(s);
    }
    if (!document.getElementById(lbScriptId)) {
      const s = document.createElement("script");
      s.id = lbScriptId;
      s.type = "application/ld+json";
      s.textContent = JSON.stringify(localBusinessSchema);
      document.head.appendChild(s);
    }

    return () => {
      document.title = "Massachusetts Real Estate & Dominican Republic Investment | Hadassah Homes";
      if (meta)
        meta.setAttribute(
          "content",
          "Buy, sell, and invest with confidence. Hadassah Homes offers real estate, interior design, and property preparation services in Massachusetts and the Dominican Republic."
        );
      if (canonical) canonical.setAttribute("href", "https://hadassahhomesinc.com");
      document.getElementById(faqScriptId)?.remove();
      document.getElementById(lbScriptId)?.remove();
    };
  }, []);

  return (
    <SiteLayout>
      <PageHero
        title="Buy a Home in Massachusetts"
        subtitle="A Smarter Way to Buy Real Estate"
        backgroundImage={heroImage}
      >
        {/* Hidden until MLS integration is live. */}
        {showPropertySearch ? <SearchFilterThin /> : null}
      </PageHero>

      <BuyerStrategySection
        subheading="Buyer Strategy"
        heading="Buying a home should feel clear, strategic, and aligned with your future."
        description="In today&apos;s market, buying well requires more than browsing listings. It takes preparation, perspective, and a plan. Hadassah Homes helps you move with clarity from the first conversation to closing day, so every step feels intentional and every decision supports your next chapter."
        image={buyGuidanceTour}
        imageAlt="Diverse Massachusetts buyers touring a polished home with a real estate advisor"
        cta={{ label: "Book a Consultation", href: "/consultation" }}
        features={[
          {
            icon: Target,
            title: "Clear next steps",
            description: "Know what happens first, what matters now, and what comes next.",
          },
          {
            icon: Compass,
            title: "Market-aware advice",
            description: "Move with stronger context instead of reacting to listings alone.",
          },
          {
            icon: Handshake,
            title: "Trusted lenders",
            description: "Access lending relationships that support faster, clearer decisions.",
          },
          {
            icon: TrendingUp,
            title: "Long-term value",
            description: "Buy with both today&apos;s fit and tomorrow&apos;s upside in mind.",
          },
        ]}
      />

      <ContentSection variant="dark" className="py-18 md:py-24 lg:py-30">
        <div className="mx-auto max-w-wide space-y-12 md:space-y-16 lg:space-y-20">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,0.95fr)_minmax(0,0.75fr)] gap-10 md:gap-14 lg:gap-20 items-start">
            <div className="space-y-6 md:space-y-7">
              <span className="font-body text-xs uppercase tracking-[0.14em] text-accent">
                Guided Experience
              </span>
              <h2 className="font-headline text-4xl md:text-5xl lg:text-6xl text-secondary-foreground leading-[1.02] max-w-3xl">
                What It&apos;s Like to Work With Us
              </h2>
            </div>

            <div className="space-y-6 md:space-y-7 lg:pt-3">
              <p className="text-secondary-foreground/82 text-base md:text-lg leading-[1.85] max-w-2xl">
                This is a more thoughtful buying experience. We combine market perspective,
                honest guidance, and strong preparation so each move feels clear, intentional,
                and aligned with your future.
              </p>
              <div className="max-w-2xl border-l border-accent/55 pl-5 md:pl-6">
                <p className="font-headline text-2xl md:text-[2rem] lg:text-[2.25rem] text-secondary-foreground leading-[1.2]">
                  Strategic, calm, and deeply personal from the first conversation to closing.
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-8 border-t border-accent/18 pt-8 md:grid-cols-2 md:gap-x-8 md:gap-y-10 md:pt-10 lg:grid-cols-4 lg:gap-10 lg:pt-12">
            {workWithUsFocus.map((item) => (
              <div key={item.title} className="space-y-3 md:space-y-4">
                <div className="h-px w-12 bg-accent/75" />
                <p className="font-body text-[10px] uppercase tracking-[0.18em] text-accent">
                  Buyer Focus
                </p>
                <p className="font-headline text-[1.65rem] md:text-[1.8rem] text-secondary-foreground leading-[1.15]">
                  {item.title}
                </p>
                <p className="max-w-sm text-secondary-foreground/78 text-sm md:text-[15px] leading-[1.85]">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </ContentSection>

      <GuideDownloadSection
        guide={BUYERS_GUIDE}
        location="buy-a-home"
        heading="Planning to Buy? Start With Our Buyer's Guide"
        body="Learn what to expect throughout the home-buying process, from preparing financially and beginning your search to making an offer and closing on your home."
      />
      <ContentSection>
        <div className="mx-auto max-w-wide">
          <div className="max-w-3xl mx-auto text-center space-y-5 md:space-y-6">
            <span className="font-body text-xs uppercase tracking-[0.14em] text-accent">Process</span>
            <h2 className="font-headline text-4xl md:text-5xl lg:text-6xl text-foreground leading-[1.04]">
              The Buying Process, Simplified
            </h2>
            <p className="text-muted-foreground text-base md:text-lg leading-[1.85] max-w-2xl mx-auto">
              We break the journey into clear stages so you always know what&apos;s next and why it matters.
            </p>
          </div>

          <div className="mt-16 md:mt-20 lg:mt-24 grid grid-cols-1 lg:grid-cols-[0.92fr_1.08fr] gap-10 md:gap-14 lg:gap-18 items-start">
            <div className="lg:sticky lg:top-32">
              <div className="overflow-hidden border border-border/60 bg-card">
                <div className="aspect-[4/5] md:aspect-[5/4] lg:aspect-[4/5] overflow-hidden">
                  <img
                    src={buyProcessConsultation}
                    alt="Diverse buyers reviewing a home search plan with an agent"
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>

            <ol className="relative list-none m-0 p-0 pl-14 md:pl-[4.75rem] space-y-10 md:space-y-12 lg:space-y-14">
              <div
                className="absolute left-[1.4rem] md:left-[1.95rem] top-0 bottom-0 w-px bg-accent/24"
                aria-hidden="true"
              />
              {buyingSteps.map((step, index) => (
                <li key={step.title} className="relative">
                  <div
                    className="absolute left-0 top-1 flex h-9 w-9 md:h-10 md:w-10 items-center justify-center border border-accent/28 bg-background text-accent"
                    aria-hidden="true"
                  >
                    <span className="font-headline text-base md:text-lg leading-none">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                  </div>

                  <div className="space-y-4 border-t border-border/55 pt-6 first:border-t-0 first:pt-0 transition-colors duration-300 hover:border-accent/36">
                    <div className="min-h-9 md:min-h-10 flex items-center gap-4 md:gap-5">
                      <span
                        className="w-9 md:w-10 shrink-0 font-headline text-base md:text-lg leading-none text-accent opacity-0"
                        aria-hidden="true"
                      >
                        {String(index + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h3 className="font-headline text-2xl md:text-[2rem] text-foreground leading-[1.12]">
                      {step.title}
                    </h3>
                    <p className="max-w-2xl text-muted-foreground text-base md:text-lg leading-[1.85]">
                      {step.description}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </ContentSection>

      <ContentSection variant="dark">
        <SplitSection
          title="First-Time Buyer? Start With Confidence"
          description="If this is your first time buying a home, you don&apos;t need to have everything figured out before reaching out."
          image={buyFirstTimeBuyer}
          imageAlt="First-time Massachusetts buyers walking through a bright home with confidence"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
            {firstTimeBuyerItems.map((item) => (
              <div key={item.title} className="bg-card/50 p-5 shadow-sm">
                <item.icon className="w-5 h-5 text-accent mb-3" strokeWidth={1.6} />
                <h3 className="font-headline text-lg text-foreground mb-2">{item.title}</h3>
                <p className="text-secondary-foreground/82 text-sm leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </SplitSection>
      </ContentSection>

      <ContentSection>
        <div className="grid grid-cols-1 items-start gap-10 md:gap-14 lg:grid-cols-[0.98fr_0.82fr] lg:gap-18">
          <div className="space-y-8">
            <div className="space-y-5">
              <span className="font-body text-xs uppercase tracking-[0.14em] text-accent">Financial Guidance</span>
              <h2 className="font-headline text-3xl md:text-5xl text-foreground leading-tight">
                Making Smart Financial Decisions
              </h2>
              <p className="text-muted-foreground text-base md:text-lg leading-[1.75]">
                Buying a home is personal, but it&apos;s also financial. We help you feel prepared, not just approved.
              </p>
            </div>

            <Checklist
              items={[
                "Understand the full monthly picture, not just the mortgage payment.",
                "Compare lending options with stronger context.",
                "Move into negotiations with more confidence and less uncertainty.",
              ]}
            />

            <ComparisonBlock
              rows={[
                "Budget clarity",
                "Financing options",
                "Monthly cost planning",
                "Loan program guidance",
                "Offer readiness",
              ]}
              columns={[
                {
                  title: "Going Alone",
                  values: [
                    "Often unclear",
                    "Limited context",
                    "Easy to underestimate",
                    "May be missed",
                    "Reactive",
                  ],
                },
                {
                  title: "With Hadassah Homes",
                  highlighted: true,
                  values: [
                    "Clear and realistic",
                    "Lender-backed options",
                    "Planned beyond mortgage",
                    "Strategically reviewed",
                    "Prepared to compete",
                  ],
                },
              ]}
            />
          </div>

          <div className="overflow-hidden border border-border/60 bg-card">
            <div className="aspect-[5/4] overflow-hidden lg:aspect-[4/5]">
              <img
                src={buyFinancialGuidance}
                alt="Diverse Massachusetts buyers reviewing financing details with a trusted advisor"
                className="h-full w-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </ContentSection>

      <ContentSection variant="dark">
        <SplitSection
          title="Buying With a Long-Term Perspective"
          description="The right home is about today, but it should still make sense years from now."
          image={buyLongTermPerspective}
          imageAlt="Massachusetts buyers evaluating a neighborhood and home exterior with an advisor"
          reverse
        >
          <Checklist
            columns={2}
            items={[
              "Location and long-term value",
              "Property condition and upgrade potential",
              "Market timing and neighborhood momentum",
              "Future resale and investment upside",
            ]}
          />
          <p className="text-secondary-foreground/82 text-base md:text-lg leading-relaxed pt-3">
            If you&apos;re also interested in{" "}
            <Link to="/invest" className="text-accent hover:text-accent/80 transition-colors underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-secondary">
              investing in real estate
            </Link>
            , we can help you evaluate homes with both lifestyle and return in mind.
          </p>
        </SplitSection>
      </ContentSection>

      <ContentSection>
        <div className="grid grid-cols-1 lg:grid-cols-[0.42fr_0.58fr] gap-12 md:gap-16 lg:gap-20 items-start">
          <div className="space-y-6 md:space-y-7">
            <SectionHeader
              eyebrow="Buyer Value"
              title="Why Buyers Choose Hadassah Homes"
              description="The experience is designed to keep you informed, prepared, and strategically positioned from start to finish."
              align="left"
            />
            <p className="text-muted-foreground text-base md:text-lg leading-[1.8]">
              Whether you&apos;re buying your first home or{" "}
              <Link to="/sell-your-home" className="text-accent hover:text-accent/80 transition-colors underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background">
                selling your home
              </Link>{" "}
              to move into something new, you deserve a process that feels polished, informed, and personal.
            </p>
            <div className="border-l border-accent/55 pl-5 md:pl-6">
              <p className="font-headline text-2xl md:text-[2rem] text-foreground leading-[1.25]">
                Guiding buyers with clarity, strategy, and confidence across Massachusetts.
              </p>
            </div>
          </div>

          <div className="space-y-8 md:space-y-9">
            {buyerValues.map((item, index) => (
              <div
                key={item.title}
                className={[
                  "space-y-4 pb-8 md:pb-9",
                  index < buyerValues.length - 1 ? "border-b border-border/55" : "",
                ].join(" ")}
              >
                <div className="flex items-start gap-4 md:gap-5">
                  <div className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center border border-accent/22 bg-accent/5 text-accent">
                    <item.icon className="h-4.5 w-4.5" strokeWidth={1.7} />
                  </div>
                  <div className="space-y-3">
                    <h3 className="font-headline text-[1.7rem] md:text-[2rem] text-foreground leading-[1.15]">
                      {item.title}
                    </h3>
                    <p className="max-w-2xl text-muted-foreground text-base md:text-lg leading-[1.8]">
                      {item.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </ContentSection>

      <ContentSection variant="dark">
        <SectionHeader
          eyebrow="Success"
          title="Recent Buyer Success"
          description="Proof points that show what thoughtful guidance can look like in the real world."
        />
        <CardGrid items={buyerSuccesses} columns={3} />
      </ContentSection>

      <TestimonialsSection
        sectionId="buyer-testimonials"
        title="What Our Buyers Say"
        testimonials={buyerTestimonials}
        pauseOnHover={false}
      />

      <ContentSection>
        <div className="grid grid-cols-1 lg:grid-cols-[0.92fr_1.08fr]">
          <div className="min-h-[340px] md:min-h-[420px] lg:min-h-[560px]">
            <img
              src={buyDROpportunity}
              alt="Diverse buyers considering a Dominican Republic ocean-view property opportunity"
              className="h-full w-full object-cover"
              loading="lazy"
            />
          </div>

          <div className="bg-cream px-8 py-10 md:px-12 md:py-14 lg:px-16 lg:py-18">
            <div className="max-w-2xl space-y-6 md:space-y-7">
              <span className="font-body text-xs uppercase tracking-[0.14em] text-accent">
                Global Opportunity
              </span>
              <h2 className="font-headline text-3xl md:text-5xl text-secondary leading-tight">
                Considering Buying Outside the U.S.?
              </h2>
              <p className="text-secondary/82 text-base md:text-lg leading-[1.8]">
                We also help buyers explore opportunities in the Dominican Republic, whether you&apos;re seeking a second home, vacation property, or investment opportunity.
              </p>
              <Link
                to="/dominican-republic"
                className="inline-flex items-center gap-2 font-body text-sm uppercase tracking-[0.12em] px-8 py-4 bg-accent text-accent-foreground hover:bg-accent/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-cream"
              >
                Explore Dominican Republic Real Estate
                <ArrowRight className="w-4 h-4" />
              </Link>

              <div className="border border-accent/20 bg-card/70 px-6 py-6 md:px-7 md:py-7">
                <div className="mb-4 flex items-center gap-3">
                  <Sparkles className="h-4 w-4 text-accent" strokeWidth={1.6} />
                  <h3 className="font-body text-sm uppercase tracking-[0.12em] text-secondary">
                    Interested in international opportunities?
                  </h3>
                </div>
                <p className="text-muted-foreground text-base leading-[1.75]">
                  We bring the same strategic guidance, market perspective, and relationship-first service to buyers exploring international real estate.
                </p>
              </div>
            </div>
          </div>
        </div>
      </ContentSection>

      <ContentSection variant="dark">
        <SectionHeader title="Frequently Asked Questions" description="Straightforward answers to the questions buyers ask most often." />
        <div className="max-w-3xl mx-auto">
          <AccordionGroup items={faqItems} />
        </div>
      </ContentSection>

      <section className="relative overflow-hidden py-20 md:py-28">
        <img
          src={buyFinalCta}
          alt="Welcoming Massachusetts home exterior with buyers approaching the front door"
          className="absolute inset-0 h-full w-full object-cover"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/35" />
        <div className="absolute inset-0 bg-gradient-to-b from-secondary/38 via-transparent to-secondary/58" />

        <div className="relative z-10 mx-auto max-w-content px-5 md:px-12">
          <div className="mx-auto max-w-3xl px-2 text-center md:px-0">
            <h2 className="font-headline text-3xl sm:text-4xl md:text-5xl leading-tight text-cream mb-5">
              Let&apos;s Find the Right Home for You
            </h2>
            <p className="text-cream text-base md:text-lg leading-[1.8] max-w-2xl mx-auto mb-10">
              Whether you&apos;re ready to begin or just want a clearer strategy, we&apos;re here to guide your next move with confidence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/consultation"
                className="inline-flex items-center justify-center font-body text-sm uppercase tracking-[0.12em] px-10 py-4 bg-accent text-accent-foreground hover:bg-accent/90 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-secondary"
              >
                Book a Consultation
              </Link>
              <Link
                to="/listings"
                className="inline-flex items-center justify-center font-body text-sm uppercase tracking-[0.12em] px-10 py-4 border border-cream/70 text-cream hover:border-cream hover:text-cream transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-secondary"
              >
                Explore Listings
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
};

export default BuyAHome;
